/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

// tslint:disable:no-non-null-assertion

import { DecimalPipe } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import {
  getGtoEntryKey,
  getUomQuantityType,
  GtoEntryPoint,
  TopDetailsDialogConfig,
  topDetailsDialogRoute,
} from '@gh/builder-shared';
import { createUomQuantity } from '@gh/builder-shared-data';
import { NumberTypes } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import { Confirmation, DialogService, yesAnswer, yesCancelConfirmation, yesNoConfirmation } from '@gh/core-ui';
import { filter$, map$ } from '@gh/rx/operators';
import { isNil } from 'lodash';
import { Observable, Subscription } from 'rxjs';
import { nullate } from '@gh/core-util';
import { GraphicalTakeOffCommandService } from '../../graphical-take-off-command.service';
import { GraphicalTakeOffFormState } from '../../graphical-take-off-form.service';
import {
  getGraphicalTakeOffTopId,
  getGraphicalTakeOffTopType,
  getGraphicalTakeOffTopUom,
  getGraphicalTakeOffTopViewLastMeasurement,
  GraphicalTakeOffBusinessService,
  GraphicalTakeOffHousePlanView,
  GraphicalTakeOffScaleDirection,
  GraphicalTakeOffTop,
  GTO_MEASUREMENT_UNITS_IN_FT,
  GtoToolName,
  isGraphicalTakeOffCountType,
  isGraphicalTakeOffTopViewValid,
  newGraphicalTakeOffImageRotateTransformation,
  ZOOM_VALUES,
} from '../../resources';

/**
 * Menu component
 */
@Component({
  selector: 'gh-graphical-take-off-menu',
  template: require('./graphical-take-off-menu.component.html'),
  styles: [require('./graphical-take-off-menu.component.scss')],
})
export class GraphicalTakeOffMenuComponent implements OnInit {
  /**
   * Entry id
   *
   * @type {ID}
   */
  @Input() entityId: ID;

  /**
   * Entry point
   *
   * @type {string}
   */
  @Input() entryPoint: GtoEntryPoint;

  /**
   * Measurement status
   *
   * @type {string}
   */
  @Input() status: string;

  /**
   * Show scale mode menu?
   *
   * @type {boolean}
   */
  @Input() scaleMode: boolean = false;

  @Input() activeTool: GtoToolName;

  /**
   * Is allow edit?
   *
   * @type {boolean}
   */
  @Input() allowEdit: boolean = false;

  /**
   * On level mode
   *
   * @type {EventEmitter<boolean>}
   */
    // tslint:disable-next-line:no-output-on-prefix
  @Output() onLevel: EventEmitter<boolean> = new EventEmitter();

  /**
   * On crop mode
   *
   * @type {EventEmitter<boolean>}
   */
    // tslint:disable-next-line:no-output-on-prefix
  @Output() onCrop: EventEmitter<boolean> = new EventEmitter();

  /**
   * On view change
   *
   * @type {EventEmitter<ID>}
   */
    // tslint:disable-next-line:no-output-on-prefix
  @Output() onView: EventEmitter<ID> = new EventEmitter();

  /**
   * On scroll view
   *
   * @type {EventEmitter<boolean>}
   */
    // tslint:disable-next-line:no-output-on-prefix
  @Output() onScrollView: EventEmitter<boolean> = new EventEmitter();

  /**
   * On overlay checked
   *
   * @type {EventEmitter<MatCheckboxChange>}
   */
    // tslint:disable-next-line:no-output-on-prefix
  @Output() onOverlay: EventEmitter<MatCheckboxChange> = new EventEmitter();

  @Output() startScaling = new EventEmitter<void>();
  @Output() stopScaling = new EventEmitter<void>();
  @Output() applyScaling = new EventEmitter<void>();

  @ViewChild('lengthInput', { read: ElementRef, static: false }) lengthInput: ElementRef;

  /**
   * Scale length
   *
   * @type {number}
   */
  length: number;

  /**
   * Scale length in inches
   *
   * @type {number}
   */
  lengthIn: number;

  /**
   * GTO entry id
   *
   * @type {string}
   */
  entryId: string;

  /**
   * GTO entry TOP id
   *
   * @type {string}
   */
  entryTOPId: string;

  zoomValues = ZOOM_VALUES;

  delta$: Observable<Nullable<number>>;

  private subscription: Subscription;

  /**
   * Constructor
   *
   * @param {DialogService} dialogService Dialog service
   * @param {GraphicalTakeOffBusinessService} gtoService GTO service
   */
  constructor(
    private dialogService: DialogService,
    private gtoService: GraphicalTakeOffBusinessService,
    private translations: TranslationService,
    private decimalPipe: DecimalPipe,
    public form: GraphicalTakeOffFormState,
    private commands: GraphicalTakeOffCommandService,
  ) {
  }

  get view(): GraphicalTakeOffHousePlanView {
    return this.form.currentView;
  }

  get top(): Maybe<GraphicalTakeOffTop> {
    return this.form.currentTop;
  }

  ngOnInit(): void {
    this.entryId = getGtoEntryKey(this.entryPoint, 'viewId');
    this.entryTOPId = getGtoEntryKey(this.entryPoint, 'topId');

    this.delta$ = this.form.scalingTool.pxLength$
      .pipe(map$((pxLength) => pxLength === 0 ? nullate(void 0) : pxLength));

    this.subscription = this.form.scalingTool.currentDirection$.subscribe(() => this.resetScalePanel());
    this.form.setUserPreference();
  }

  getTopValueInCurrentView(top: GraphicalTakeOffTop): number {
    return this.form.getTopValueInCurrentView(getGraphicalTakeOffTopId(top));
  }

  getTopValueInAllViews(top: GraphicalTakeOffTop): number {
    return this.form.getTopValueInAllViews(getGraphicalTakeOffTopId(top));
  }

  isTopChanged(top: GraphicalTakeOffTop): boolean {
    return this.form.isTopChanged(getGraphicalTakeOffTopId(top));
  }

  /**
   * Is scale results calculated?
   *
   * @returns {boolean} - Is scale results calculated?
   */
  get scaleCalculated(): boolean {
    return this.form.isScaleCompleted;
  }

  /**
   * Is calculated scale valid?
   *
   * @returns {boolean} - Is calculated scale valid?
   */
  get scaleValid(): boolean {
    const percent = 100;
    const maxDiff = 5; // Max diff between horizontal and vertical scales

    if (!this.view || !this.view.picture || !this.form.isScaleCompleted) {
      return false;
    }

    const { currentScaleWidth, currentScaleHeight } = this.form;

    return (
      Math.abs(currentScaleWidth - currentScaleHeight) * percent
      / Math.max(currentScaleWidth, currentScaleHeight)
    ) <= maxDiff;
  }

  /**
   * Is view operations disabled (rotate, crop, etc)?
   * Returns false if active view doesn't have uploaded picture
   *
   * @returns {boolean} - Is view operations disabled?
   */
  get viewOperationsDisabled(): boolean {
    return !this.view || !this.view.picture || !this.allowEdit;
  }

  /**
   * Is view options disabled (zoom, fullscreen, etc)?
   * Returns false if active view doesn't have calculated scale
   * or component is in scale measurement mode
   *
   * @returns {boolean} - Is view options disabled?
   */
  get viewOptionsDisabled(): boolean {
    return this.scaleMode || this.form.isSetScaleToolActive || !this.allowEdit;
  }

  get viewOptionsDisabledOverlayAndTop(): boolean {
    return this.scaleMode || this.form.isSetScaleToolActive;
  }

  /**
   * Is set scale mode?
   *
   * @returns {boolean}
   */
  get isSetScaleToolActive(): boolean {
    return this.form.isSetScaleToolActive;
  }

  /**
   * Is imperial units?
   *
   * @returns {boolean}
   */
  get isImperialUnits(): boolean {
    return this.view && this.gtoService.isImperialUnits(this.view);
  }

  /**
   * Is zoom disabled?
   *
   * @returns {boolean}
   */
  get zoomDisabled(): boolean {
    return !this.view || !this.allowEdit;
  }

  /**
   * Is view selection disabled (scroll view, etc)?
   * Returns false if views count less than 2 or component is in scale measurement mode
   *
   * @returns {boolean} - Is view selection disabled?
   */
  get viewSelectionDisabled(): boolean {
    const minViews = 2;
    return this.form.views.length < minViews;
  }

  get scaleVectorDisabled(): boolean {
    return this.form.isScaleLineWaitingForLength ? this.setScaleDisabled : false;
  }

  get setScaleDisabled(): boolean {
    return this.isImperialUnits ? !this.length || isNil(this.lengthIn) : !this.length;
  }

  get topSelectDisabled(): boolean {
    const { drawingTop, drawingMeasurement } = this.form;
    if (isNil(drawingTop) || isNil(drawingMeasurement)) {
      return false;
    }

    return !isGraphicalTakeOffTopViewValid(drawingTop, drawingMeasurement);
  }

  /**
   * Get active view value
   *
   * @returns {string} - Value
   */
  get activeViewValue(): ID {
    return this.form.selectedViewId;
  }

  /**
   * Get selection value
   *
   * @returns {string}
   */
  get selectionValue(): string {
    const { currentTopView } = this.form;

    if (isNil(currentTopView) || isGraphicalTakeOffCountType(getGraphicalTakeOffTopType(currentTopView))) {
      return 'N/A';
    }

    const selection = getGraphicalTakeOffTopViewLastMeasurement(currentTopView);
    if (isNil(selection)) {
      return 'N/A';
    }

    const { value } = selection;

    const uom = getGraphicalTakeOffTopUom(currentTopView);
    const type = getUomQuantityType(uom);

    const total = type.format(createUomQuantity(value || 0, uom));
    const isChanged = selection.isChanged ? '*' : '';

    return `${total} ${isChanged}`;
  }

  /**
   * Get view total value
   *
   * @returns {string}
   */
  get totalViewValue(): string {
    const { currentTop } = this.form;

    if (isNil(currentTop)) {
      return 'N/A';
    }

    const value = this.getTopValueInCurrentView(currentTop);
    const uom = getGraphicalTakeOffTopUom(currentTop);
    const type = getUomQuantityType(uom);

    const total = type.format(createUomQuantity(value || 0, uom));
    const isChanged = this.isTopChanged(currentTop) ? '*' : '';

    return `${total} ${isChanged}`;
  }

  /**
   * Get all views total value
   *
   * @returns {string}
   */
  get totalAllValue(): string {
    const { currentTop } = this.form;

    if (isNil(currentTop)) {
      return 'N/A';
    }

    const value = this.getTopValueInAllViews(currentTop);

    const uom = getGraphicalTakeOffTopUom(currentTop);
    const type = getUomQuantityType(uom);

    const total = type.format(createUomQuantity(value || 0, uom));
    const isChanged = this.isTopChanged(currentTop) ? '*' : '';

    return `${total} ${isChanged}`;
  }

  /**
   * Get measurement default units
   *
   * @returns {string}
   */
  getUnitsLabel(label: string): string {
    let units = '';
    const { currentTop } = this.form;

    if (currentTop && currentTop.gtoUom) {
      units = `, ${currentTop.gtoUom.toUpperCase()}`;
    }

    return this.translations.get(
      label,
      { units: `${units}` });
  }

  getDefaultUnitsLabel(): string {
    return this.translations.get(
      'lbl_gto_length',
      { units: this.view.measurementDefault || '' });
  }

  /**
   * Rotate raster left
   *
   * @returns {void}
   */
  rotateLeft(): void {
    // tslint:disable-next-line:no-magic-numbers
    this.commands.currentViewImageTransform(newGraphicalTakeOffImageRotateTransformation(-90));
  }

  /**
   * Rotate raster right
   *
   * @returns {void}
   */
  rotateRight(): void {
    // tslint:disable-next-line:no-magic-numbers
    this.commands.currentViewImageTransform(newGraphicalTakeOffImageRotateTransformation(90));
  }

  /**
   * Set level
   *
   * @returns {void}
   */
  level(): void {
    this.onLevel.emit(false);
  }

  /**
   * Crop
   *
   * @returns {void}
   */
  crop(): void {
    this.onCrop.emit(false);
  }

  /**
   * Zoom
   *
   * @param {number} scaleFactor - Scale factor
   * @returns {void}
   */
  zoom(scaleFactor: number): void {
    this.commands.setZoom(scaleFactor);
  }

  /**
   * Change view
   *
   * @param {ID} id - View id
   * @returns {void}
   */
  viewChange(id: ID): void {
    if (!this.commands.tryToFinalizeMeasurement()) {
      return;
    }

    this.onView.emit(id);
  }

  /**
   * Scroll view
   *
   * @returns {void}
   */
  scrollView(): void {
    this.onScrollView.emit(true);
  }

  /**
   * Restart view
   *
   * @returns {void}
   */
  restart(): void {
    this.commands.currentViewResetTransformations();
  }

  /**
   * Overlay mode
   *
   * @param {MatCheckboxChange} change - Checkbox change
   * @returns {void}
   */
  overlay(change: MatCheckboxChange): void {
    this.onOverlay.emit(change);
  }

  /**
   * Snap to point mode
   *
   * @param {MatCheckboxChange} change - Checkbox change
   * @returns {void}
   */
  snapToPoint(change: MatCheckboxChange): void {
    this.commands.updateUserPreference({snapToPoint:change.checked});
    this.commands.setSnapToPoint(change.checked);
  }

  /**
   * Change TOP
   *
   * @param {GraphicalTakeOffTop} top - TOP
   * @returns {void}
   */
  topChange(top: GraphicalTakeOffTop): void {
    this.commands.setCurrentTop(getGraphicalTakeOffTopId(top));
  }

  /**
   * Create new ToP
   *
   * @returns {void}
   */
  addTop(): void {
    const input: TopDetailsDialogConfig = {
      entryPoint: this.entryPoint,
      entryId: this.entityId,
    };

    this.dialogService.openWithInput(topDetailsDialogRoute, input)
      .afterClosed().pipe(
      filter$(Boolean))
      .subscribe((top: GraphicalTakeOffTop) => {
        this.commands.addTop(top);
        this.topChange(top);
      });
  }

  onKeySetScale(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !this.setScaleDisabled) {
      this.onSetScaleLineLength();
    }
  }

  onChangeScaleDirection(direction: GraphicalTakeOffScaleDirection): void {
    this.commands.setScaleLineDirection(direction);
  }

  onSetScaleLineLength(): void {
    if (this.setScaleDisabled || !this.view.picture) {
      return;
    }

    const scaleResult = (
      this.isImperialUnits ? this.length + this.lengthIn / GTO_MEASUREMENT_UNITS_IN_FT : this.length
    ) / this.form.scaleLinePxLength;

    this.commands.setScaleLineLength(scaleResult);
  }

  /**
   * Validate scale results ans close scale mode
   *
   * @returns {void}
   */
  applyScale(): void {
    const apply = () => {
      this.commands.setViewScale(this.form.currentScaleWidth, this.form.currentScaleHeight);
      this.applyScaling.emit();
    };
    const reset = () => {
      this.commands.setViewScale(this.form.previousScaleWidth, this.form.previousScaleHeight);
      this.stopScaling.emit();
    };

    const onScaleValid = () => {
      if (!this.scaleValid) {
        this.dialogService
          .confirmation(yesNoConfirmation('msg_gto_differences_in_scale'))
          .subscribe((confirmation) => {
            if (yesAnswer(confirmation)) {
              apply();
            } else {
              this.form.resetScale();
              reset();
            }
          });
      } else {
        apply();
      }
    };

    if (this.form.hasDimensionMeasurements) {
      this.dialogService
        .confirmation(yesCancelConfirmation('msg_gto_scale_top_recalculate'))
        .subscribe((answer) => {
          switch (answer) {
            case Confirmation.Yes:
              onScaleValid();
              break;
            case Confirmation.Cancel:
              this.form.resetScale();
              reset();
              break;
            default:
              break;
          }
        });
    } else {
      onScaleValid();
    }
  }

  private getSelectionImperial(value: any): string {
    const totalSelection = Number(value);
    const heightFt = Math.floor(totalSelection);
    const heightIn = NumberTypes.Length.format((totalSelection - heightFt) * GTO_MEASUREMENT_UNITS_IN_FT);
    return this.translations.get('lbl_ft_in_sign', { ft: `${heightFt}`, in: `${heightIn}` });
  }

  private resetScalePanel(): void {
    this.length = <any>void 0;
    this.lengthIn = <any>void 0;
  }
}
